import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Barcode, Download, Loader2, ShieldCheck, Plus, Trash2, Upload, Layers, Rows,
} from 'lucide-react';
import AdminLayout from '../components/AdminLayout';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Standard39 charset — anything outside gets flagged in the UI before
// the user hits Export PDF.
const STANDARD39_RE = /^[A-Z0-9\-.\s$/+%]+$/;

const padNumber = (n, digits) => String(n).padStart(digits, '0');

/**
 * Feb 2026 v2 — Barcode 39 Label Printing utility.
 *
 * Batch generation: every label in the review queue is rendered on
 * its own PDF page, sized to the user's cm inputs. Advanced sequence
 * modes (Location Range, Simple Range), Excel/CSV import, and a
 * Stacked-vs-Classic layout dropdown.
 */
export default function AdminLabelPrint() {
  const authHeaders = useMemo(
    () => ({ Authorization: `Bearer ${localStorage.getItem('mr_admin_token') || ''}` }),
    [],
  );

  const [meLoaded, setMeLoaded] = useState(false);
  const [hasPerm, setHasPerm] = useState(false);
  const [palletIds, setPalletIds] = useState([]);
  const [locationIds, setLocationIds] = useState([]);

  // Queue + config
  const [queue, setQueue] = useState([]); // [{ id, text }]
  const [widthCm, setWidthCm] = useState(10);
  const [heightCm, setHeightCm] = useState(5);
  const [layout, setLayout] = useState('classic');
  const [busy, setBusy] = useState(false);

  // Source mode: 'pallet' | 'location' | 'custom' | 'location_range' | 'simple_range' | 'excel'
  const [mode, setMode] = useState('pallet');
  const [palletChoice, setPalletChoice] = useState('');
  const [locationChoice, setLocationChoice] = useState('');
  const [customText, setCustomText] = useState('');

  // Location range mode
  const [rangeZone, setRangeZone] = useState('GROUND');
  const [rangeSubZone, setRangeSubZone] = useState('A');
  const [rangeStart, setRangeStart] = useState(1);
  const [rangeEnd, setRangeEnd] = useState(10);
  const [rangePad, setRangePad] = useState(3);

  // Simple range mode
  const [simplePrefix, setSimplePrefix] = useState('PLT');
  const [simpleStart, setSimpleStart] = useState(1);
  const [simpleEnd, setSimpleEnd] = useState(20);
  const [simplePad, setSimplePad] = useState(6);

  const fileInputRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const me = await axios.get(`${API}/admin/me`, { headers: authHeaders });
        const superFlag = !!me.data?.is_super_admin;
        const perms = Array.isArray(me.data?.permissions) ? me.data.permissions : [];
        const wildcard = perms.includes('*');
        const bareWarehouse = perms.includes('warehouse');
        const allowed = superFlag || wildcard || bareWarehouse || perms.includes('warehouse.label_print');
        setHasPerm(allowed);
      } catch { /* leave defaults */ }
      setMeLoaded(true);
    })();
  }, [authHeaders]);

  useEffect(() => {
    if (!hasPerm) return;
    (async () => {
      try {
        const r = await axios.get(`${API}/admin/warehouse/label-print/options`, { headers: authHeaders });
        setPalletIds(r.data?.pallet_ids || []);
        setLocationIds(r.data?.location_ids || []);
      } catch { toast.error('Could not load pallet / location lists'); }
    })();
  }, [hasPerm, authHeaders]);

  // Dedupe (case-insensitive), preserve order.
  const enqueue = (values) => {
    const cleaned = values.map((v) => String(v || '').trim()).filter(Boolean);
    if (!cleaned.length) return 0;
    setQueue((prev) => {
      const existing = new Set(prev.map((r) => r.text.toUpperCase()));
      const next = [...prev];
      for (const v of cleaned) {
        const key = v.toUpperCase();
        if (existing.has(key)) continue;
        existing.add(key);
        next.push({ id: `q_${Date.now()}_${next.length}_${Math.random().toString(36).slice(2, 6)}`, text: v });
      }
      return next;
    });
    return cleaned.length;
  };

  const removeFromQueue = (id) => setQueue((prev) => prev.filter((r) => r.id !== id));
  const clearQueue = () => setQueue([]);

  const addCurrent = () => {
    let vals = [];
    if (mode === 'pallet') vals = [palletChoice];
    else if (mode === 'location') vals = [locationChoice];
    else if (mode === 'custom') vals = [customText];
    else if (mode === 'location_range') {
      const s = Number(rangeStart);
      const e = Number(rangeEnd);
      if (!Number.isFinite(s) || !Number.isFinite(e) || s > e) return toast.error('Invalid range');
      if (e - s + 1 > 500) return toast.error('Range too large (max 500 per add)');
      const zone = (rangeZone || '').trim();
      const sub = (rangeSubZone || '').trim();
      if (!zone || !sub) return toast.error('Zone and Sub-Zone are required');
      vals = [];
      for (let i = s; i <= e; i += 1) vals.push(`${zone}-${sub}-${padNumber(i, Number(rangePad) || 0)}`);
    } else if (mode === 'simple_range') {
      const s = Number(simpleStart);
      const e = Number(simpleEnd);
      if (!Number.isFinite(s) || !Number.isFinite(e) || s > e) return toast.error('Invalid range');
      if (e - s + 1 > 500) return toast.error('Range too large (max 500 per add)');
      const p = (simplePrefix || '').trim();
      if (!p) return toast.error('Prefix is required');
      vals = [];
      for (let i = s; i <= e; i += 1) vals.push(`${p}-${padNumber(i, Number(simplePad) || 0)}`);
    }
    const added = enqueue(vals);
    if (added > 0) toast.success(`Added ${added} label${added === 1 ? '' : 's'}`);
    else if (mode !== 'excel') toast.info('Nothing to add');
    return null;
  };

  const importExcel = async (file) => {
    if (!file) return;
    const form = new FormData();
    form.append('file', file);
    try {
      const r = await axios.post(`${API}/admin/warehouse/label-print/import`, form, {
        headers: { ...authHeaders, 'Content-Type': 'multipart/form-data' },
      });
      const labels = r.data?.labels || [];
      const added = enqueue(labels);
      toast.success(`Imported ${added} label${added === 1 ? '' : 's'} from ${file.name}`);
    } catch (e) {
      toast.error(String(e?.response?.data?.detail || 'Import failed'));
    }
  };

  const invalidCount = useMemo(
    () => queue.filter((r) => !STANDARD39_RE.test(r.text.toUpperCase())).length,
    [queue],
  );
  const canExport = queue.length > 0 && invalidCount === 0 && Number(widthCm) > 0 && Number(heightCm) > 0 && !busy;

  const exportPdf = async () => {
    if (!canExport) return;
    setBusy(true);
    try {
      const res = await axios.post(
        `${API}/admin/warehouse/label-print/pdf`,
        {
          labels: queue.map((r) => r.text),
          width_cm: Number(widthCm),
          height_cm: Number(heightCm),
          layout,
        },
        { headers: authHeaders, responseType: 'blob' },
      );
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const suffix = queue.length === 1
        ? queue[0].text.replace(/[^A-Za-z0-9._-]+/g, '_').slice(0, 32)
        : `x${queue.length}`;
      a.download = `labels-${suffix || 'labels'}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${queue.length} label${queue.length === 1 ? '' : 's'}`);
    } catch (e) {
      toast.error(String(e?.response?.data?.detail || 'Could not generate PDF'));
    } finally {
      setBusy(false);
    }
  };

  if (!meLoaded) {
    return (
      <AdminLayout>
        <div className="p-8 text-slate-500 text-sm" data-testid="label-print-loading">Loading…</div>
      </AdminLayout>
    );
  }
  if (!hasPerm) {
    return (
      <AdminLayout>
        <div className="p-8" data-testid="label-print-forbidden">
          <div className="max-w-md mx-auto rounded-xl border-2 border-rose-200 bg-rose-50 p-6 text-center">
            <ShieldCheck className="w-8 h-8 text-rose-500 mx-auto mb-2" />
            <div className="font-bold text-rose-800">Not authorised</div>
            <div className="text-sm text-rose-700 mt-1">Ask a super admin to grant <code>warehouse.label_print</code>.</div>
          </div>
        </div>
      </AdminLayout>
    );
  }

  const modeButtons = [
    { key: 'pallet', label: 'Pallet ID' },
    { key: 'location', label: 'Location ID' },
    { key: 'custom', label: 'Custom Text' },
    { key: 'location_range', label: 'Location Range' },
    { key: 'simple_range', label: 'Simple Range' },
    { key: 'excel', label: 'Excel Import' },
  ];

  return (
    <AdminLayout>
      <div className="p-6 max-w-6xl mx-auto" data-testid="label-print-page">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-[#0078D4]/10 flex items-center justify-center">
            <Barcode className="w-5 h-5 text-[#0078D4]" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-slate-900">Label Printing</h1>
            <div className="text-xs text-slate-500">Barcode 39 · multi-page PDF · sequences, imports, and side-by-side layouts</div>
          </div>
        </div>

        <div className="grid gap-4 lg:grid-cols-5">
          {/* Left column — source + config */}
          <div className="lg:col-span-3 space-y-4">
            {/* Source picker */}
            <div className="rounded-xl border-2 border-slate-200 bg-white p-5">
              <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-2">Add labels</div>
              <div className="flex flex-wrap gap-2">
                {modeButtons.map((opt) => (
                  <button
                    key={opt.key}
                    type="button"
                    onClick={() => setMode(opt.key)}
                    className={`h-9 px-3 rounded-lg border-2 text-xs font-semibold transition ${
                      mode === opt.key
                        ? 'border-[#0078D4] bg-[#0078D4]/10 text-[#0078D4]'
                        : 'border-slate-200 text-slate-700 hover:border-slate-300'
                    }`}
                    data-testid={`label-print-mode-${opt.key}`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>

              <div className="mt-4">
                {mode === 'pallet' && (
                  <div className="flex gap-2 items-end">
                    <label className="block flex-1">
                      <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Pallet ID</span>
                      <select value={palletChoice} onChange={(e) => setPalletChoice(e.target.value)} className="mt-1 w-full h-11 rounded-lg border-2 border-slate-200 focus:border-[#0078D4] focus:outline-none px-3 text-sm font-mono bg-white" data-testid="label-print-pallet-select">
                        <option value="">— Select pallet —</option>
                        {palletIds.map((p) => <option key={p} value={p}>{p}</option>)}
                      </select>
                    </label>
                    <button type="button" onClick={addCurrent} disabled={!palletChoice} className="h-11 px-4 rounded-lg bg-[#0078D4] hover:bg-[#005FA3] text-white text-sm font-semibold disabled:bg-slate-300 disabled:cursor-not-allowed flex items-center gap-1" data-testid="label-print-add-btn"><Plus className="w-4 h-4" />Add</button>
                  </div>
                )}
                {mode === 'location' && (
                  <div className="flex gap-2 items-end">
                    <label className="block flex-1">
                      <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Location ID</span>
                      <select value={locationChoice} onChange={(e) => setLocationChoice(e.target.value)} className="mt-1 w-full h-11 rounded-lg border-2 border-slate-200 focus:border-[#0078D4] focus:outline-none px-3 text-sm font-mono bg-white" data-testid="label-print-location-select">
                        <option value="">— Select location —</option>
                        {locationIds.map((l) => <option key={l} value={l}>{l}</option>)}
                      </select>
                    </label>
                    <button type="button" onClick={addCurrent} disabled={!locationChoice} className="h-11 px-4 rounded-lg bg-[#0078D4] hover:bg-[#005FA3] text-white text-sm font-semibold disabled:bg-slate-300 disabled:cursor-not-allowed flex items-center gap-1"><Plus className="w-4 h-4" />Add</button>
                  </div>
                )}
                {mode === 'custom' && (
                  <div className="flex gap-2 items-end">
                    <label className="block flex-1">
                      <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Custom Text <span className="text-slate-400 font-normal normal-case tracking-normal">(A-Z 0-9 space - . $ / + %)</span></span>
                      <input type="text" value={customText} onChange={(e) => setCustomText(e.target.value)} placeholder="e.g. MR-00000123" className="mt-1 w-full h-11 rounded-lg border-2 border-slate-200 focus:border-[#0078D4] focus:outline-none px-3 text-sm font-mono" data-testid="label-print-custom-input" maxLength={64} />
                    </label>
                    <button type="button" onClick={() => { addCurrent(); setCustomText(''); }} disabled={!customText.trim()} className="h-11 px-4 rounded-lg bg-[#0078D4] hover:bg-[#005FA3] text-white text-sm font-semibold disabled:bg-slate-300 disabled:cursor-not-allowed flex items-center gap-1"><Plus className="w-4 h-4" />Add</button>
                  </div>
                )}
                {mode === 'location_range' && (
                  <div className="grid grid-cols-1 sm:grid-cols-6 gap-2 items-end">
                    <label className="block sm:col-span-2"><span className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Zone</span><input value={rangeZone} onChange={(e) => setRangeZone(e.target.value)} className="mt-1 w-full h-11 rounded-lg border-2 border-slate-200 focus:border-[#0078D4] focus:outline-none px-3 text-sm font-mono" data-testid="label-print-zone-input" /></label>
                    <label className="block sm:col-span-2"><span className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Sub-Zone</span><input value={rangeSubZone} onChange={(e) => setRangeSubZone(e.target.value)} className="mt-1 w-full h-11 rounded-lg border-2 border-slate-200 focus:border-[#0078D4] focus:outline-none px-3 text-sm font-mono" data-testid="label-print-subzone-input" /></label>
                    <label className="block"><span className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Pad</span><input type="number" min="0" max="6" value={rangePad} onChange={(e) => setRangePad(e.target.value)} className="mt-1 w-full h-11 rounded-lg border-2 border-slate-200 focus:border-[#0078D4] focus:outline-none px-3 text-sm" /></label>
                    <label className="block"><span className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Start</span><input type="number" value={rangeStart} onChange={(e) => setRangeStart(e.target.value)} className="mt-1 w-full h-11 rounded-lg border-2 border-slate-200 focus:border-[#0078D4] focus:outline-none px-3 text-sm" data-testid="label-print-range-start" /></label>
                    <label className="block"><span className="text-[10px] uppercase tracking-wider font-bold text-slate-500">End</span><input type="number" value={rangeEnd} onChange={(e) => setRangeEnd(e.target.value)} className="mt-1 w-full h-11 rounded-lg border-2 border-slate-200 focus:border-[#0078D4] focus:outline-none px-3 text-sm" data-testid="label-print-range-end" /></label>
                    <div className="sm:col-span-6 flex items-center justify-between">
                      <div className="text-xs text-slate-500 font-mono">
                        Preview: <span className="text-slate-800">{`${rangeZone || 'ZONE'}-${rangeSubZone || 'SUB'}-${padNumber(rangeStart || 0, Number(rangePad) || 0)}`}</span> … <span className="text-slate-800">{`${rangeZone || 'ZONE'}-${rangeSubZone || 'SUB'}-${padNumber(rangeEnd || 0, Number(rangePad) || 0)}`}</span>
                      </div>
                      <button type="button" onClick={addCurrent} className="h-11 px-4 rounded-lg bg-[#0078D4] hover:bg-[#005FA3] text-white text-sm font-semibold flex items-center gap-1" data-testid="label-print-range-add"><Plus className="w-4 h-4" />Generate & Add</button>
                    </div>
                  </div>
                )}
                {mode === 'simple_range' && (
                  <div className="grid grid-cols-1 sm:grid-cols-5 gap-2 items-end">
                    <label className="block sm:col-span-2"><span className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Prefix</span><input value={simplePrefix} onChange={(e) => setSimplePrefix(e.target.value)} className="mt-1 w-full h-11 rounded-lg border-2 border-slate-200 focus:border-[#0078D4] focus:outline-none px-3 text-sm font-mono" data-testid="label-print-simple-prefix" /></label>
                    <label className="block"><span className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Pad</span><input type="number" min="0" max="8" value={simplePad} onChange={(e) => setSimplePad(e.target.value)} className="mt-1 w-full h-11 rounded-lg border-2 border-slate-200 focus:border-[#0078D4] focus:outline-none px-3 text-sm" /></label>
                    <label className="block"><span className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Start</span><input type="number" value={simpleStart} onChange={(e) => setSimpleStart(e.target.value)} className="mt-1 w-full h-11 rounded-lg border-2 border-slate-200 focus:border-[#0078D4] focus:outline-none px-3 text-sm" data-testid="label-print-simple-start" /></label>
                    <label className="block"><span className="text-[10px] uppercase tracking-wider font-bold text-slate-500">End</span><input type="number" value={simpleEnd} onChange={(e) => setSimpleEnd(e.target.value)} className="mt-1 w-full h-11 rounded-lg border-2 border-slate-200 focus:border-[#0078D4] focus:outline-none px-3 text-sm" data-testid="label-print-simple-end" /></label>
                    <div className="sm:col-span-5 flex items-center justify-between">
                      <div className="text-xs text-slate-500 font-mono">
                        Preview: <span className="text-slate-800">{`${simplePrefix || 'PREFIX'}-${padNumber(simpleStart || 0, Number(simplePad) || 0)}`}</span> … <span className="text-slate-800">{`${simplePrefix || 'PREFIX'}-${padNumber(simpleEnd || 0, Number(simplePad) || 0)}`}</span>
                      </div>
                      <button type="button" onClick={addCurrent} className="h-11 px-4 rounded-lg bg-[#0078D4] hover:bg-[#005FA3] text-white text-sm font-semibold flex items-center gap-1" data-testid="label-print-simple-add"><Plus className="w-4 h-4" />Generate & Add</button>
                    </div>
                  </div>
                )}
                {mode === 'excel' && (
                  <div className="rounded-lg border-2 border-dashed border-slate-300 bg-slate-50 p-4">
                    <div className="text-sm text-slate-700 mb-2">Upload an <b>.xlsx</b> or <b>.csv</b> file. First matching column (`code`, `label`, `barcode`, `pallet_id`, `location_id`, `sku`, `text`) is used; otherwise the first non-empty cell of each row.</div>
                    <input ref={fileInputRef} type="file" accept=".xlsx,.csv" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) importExcel(f); e.target.value = ''; }} data-testid="label-print-excel-input" />
                    <button type="button" onClick={() => fileInputRef.current?.click()} className="h-10 px-4 rounded-lg bg-slate-800 hover:bg-slate-900 text-white text-sm font-semibold flex items-center gap-2" data-testid="label-print-excel-btn"><Upload className="w-4 h-4" />Choose file to import</button>
                  </div>
                )}
              </div>
            </div>

            {/* Dimensions + layout */}
            <div className="rounded-xl border-2 border-slate-200 bg-white p-5">
              <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-2">Output</div>
              <div className="grid gap-3 sm:grid-cols-3">
                <label className="block"><span className="text-[11px] text-slate-500">Width (cm)</span><input type="number" min="1" max="30" step="0.1" value={widthCm} onChange={(e) => setWidthCm(e.target.value)} className="mt-1 w-full h-11 rounded-lg border-2 border-slate-200 focus:border-[#0078D4] focus:outline-none px-3 text-sm" data-testid="label-print-width-input" /></label>
                <label className="block"><span className="text-[11px] text-slate-500">Height (cm)</span><input type="number" min="1" max="30" step="0.1" value={heightCm} onChange={(e) => setHeightCm(e.target.value)} className="mt-1 w-full h-11 rounded-lg border-2 border-slate-200 focus:border-[#0078D4] focus:outline-none px-3 text-sm" data-testid="label-print-height-input" /></label>
                <label className="block">
                  <span className="text-[11px] text-slate-500 flex items-center gap-1">Layout {layout === 'stacked' ? <Layers className="w-3 h-3" /> : <Rows className="w-3 h-3" />}</span>
                  <select value={layout} onChange={(e) => setLayout(e.target.value)} className="mt-1 w-full h-11 rounded-lg border-2 border-slate-200 focus:border-[#0078D4] focus:outline-none px-3 text-sm bg-white" data-testid="label-print-layout-select">
                    <option value="classic">Classic (barcode + text)</option>
                    <option value="stacked">Stacked (barcode top / text bottom)</option>
                  </select>
                </label>
              </div>
            </div>
          </div>

          {/* Right column — queue */}
          <div className="lg:col-span-2">
            <div className="rounded-xl border-2 border-slate-200 bg-white p-4 sticky top-4" data-testid="label-print-queue-card">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Labels in queue</div>
                  <div className="text-lg font-bold text-slate-900" data-testid="label-print-queue-count">{queue.length}</div>
                </div>
                {queue.length > 0 && (
                  <button type="button" onClick={clearQueue} className="h-8 px-3 rounded-md border border-rose-200 text-rose-600 hover:bg-rose-50 text-xs font-semibold" data-testid="label-print-clear-btn">Clear all</button>
                )}
              </div>
              {invalidCount > 0 && (
                <div className="mb-2 text-[11px] rounded-md bg-amber-50 border border-amber-200 text-amber-800 px-2 py-1">
                  {invalidCount} label{invalidCount === 1 ? '' : 's'} contain characters outside Barcode 39. Remove or edit before exporting.
                </div>
              )}
              <div className="max-h-80 overflow-auto border rounded-lg" data-testid="label-print-queue-list">
                {queue.length === 0 ? (
                  <div className="p-4 text-center text-xs text-slate-400">Nothing queued yet.</div>
                ) : (
                  <table className="w-full text-xs">
                    <thead className="bg-slate-50 text-slate-500 uppercase tracking-wider text-[10px]">
                      <tr><th className="p-2 text-left w-10">#</th><th className="p-2 text-left">Code</th><th className="p-2 w-10"></th></tr>
                    </thead>
                    <tbody>
                      {queue.map((r, i) => {
                        const invalid = !STANDARD39_RE.test(r.text.toUpperCase());
                        return (
                          <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`label-print-queue-row-${i}`}>
                            <td className="p-2 text-slate-400 font-mono">{i + 1}</td>
                            <td className={`p-2 font-mono ${invalid ? 'text-rose-600' : 'text-slate-800'}`}>{r.text}</td>
                            <td className="p-2 text-right"><button type="button" onClick={() => removeFromQueue(r.id)} className="text-slate-400 hover:text-rose-600" aria-label="Remove"><Trash2 className="w-3.5 h-3.5" /></button></td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
              <button type="button" onClick={exportPdf} disabled={!canExport} className={`mt-3 w-full h-11 rounded-lg font-semibold text-sm text-white flex items-center justify-center gap-2 transition ${canExport ? 'bg-[#0078D4] hover:bg-[#005FA3]' : 'bg-slate-300 cursor-not-allowed'}`} data-testid="label-print-download-btn">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                Export PDF{queue.length ? ` (${queue.length} page${queue.length === 1 ? '' : 's'})` : ''}
              </button>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
